import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatBrlCurrency } from "@/lib/price";
import { calculateBomCostCents, calculateLineCostCents } from "@/lib/pricing";
import type { RawItem } from "@/types/models";
import { RawItemTypeahead } from "./RawItemTypeahead";

export interface ProductBomLine {
  rawItemId: string;
  quantity: string;
}

export interface ProductBillOfMaterialsEditorProps {
  rawItems: RawItem[];
  lines: ProductBomLine[];
  onChange: (lines: ProductBomLine[]) => void;
  showHeader?: boolean;
}

function emptyLine(): ProductBomLine {
  return { rawItemId: "", quantity: "1" };
}

const fieldLabelClassName =
  "text-xs font-light tracking-widest uppercase leading-snug text-primary-600 dark:text-primary-400";

function parseQuantity(value: string): number {
  const normalized = value.trim().replace(",", ".");
  const parsed = Number.parseFloat(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function ProductBillOfMaterialsEditor({
  rawItems,
  lines,
  onChange,
  showHeader = true,
}: ProductBillOfMaterialsEditorProps) {
  const rawItemById = useMemo(
    () => new Map(rawItems.map((item) => [item.id, item])),
    [rawItems]
  );

  const totalCostCents = useMemo(() => {
    const bomLines = lines
      .map((line) => {
        const rawItem = rawItemById.get(line.rawItemId);
        if (!rawItem) return null;
        const quantity = parseQuantity(line.quantity);
        if (quantity <= 0) return null;
        return { costCents: rawItem.costCents, quantity };
      })
      .filter((line): line is { costCents: number; quantity: number } => line !== null);

    return calculateBomCostCents(bomLines);
  }, [lines, rawItemById]);

  const updateLine = (index: number, patch: Partial<ProductBomLine>) => {
    onChange(lines.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const removeLine = (index: number) => {
    onChange(lines.filter((_, i) => i !== index));
  };

  const addLine = () => {
    onChange([...lines, emptyLine()]);
  };

  return (
    <section className="space-y-4" data-testid="product-bom-editor">
      {showHeader ? (
        <div>
          <h3 className="text-xs font-light tracking-widest uppercase text-primary-600 dark:text-primary-400 mb-1">
            Receita
          </h3>
          <p className="text-sm font-light text-primary-600 dark:text-primary-400">
            Componha o produto a partir dos insumos cadastrados.
          </p>
        </div>
      ) : (
        <div>
          <h4 className="text-xs font-light tracking-widest uppercase text-primary-600 dark:text-primary-400 mb-1">
            Receita
          </h4>
          <p className="text-sm font-light text-primary-500 dark:text-primary-500">
            Componha o produto a partir dos insumos cadastrados.
          </p>
        </div>
      )}

      {lines.length === 0 ? (
        <p className="text-sm font-light text-primary-500 dark:text-primary-500">
          Nenhum insumo na receita. Adicione linhas para calcular o custo automaticamente.
        </p>
      ) : (
        <ul className="space-y-3">
          {lines.map((line, index) => {
            const rawItem = rawItemById.get(line.rawItemId);
            const quantity = parseQuantity(line.quantity);
            const lineCostCents =
              rawItem && quantity > 0
                ? calculateLineCostCents(rawItem.costCents, quantity)
                : null;

            return (
              <li
                key={index}
                className="border border-primary-200 dark:border-primary-800 p-4"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_120px_120px_auto] sm:gap-x-4">
                  <div className="flex min-w-0 flex-col gap-2">
                    <label htmlFor={`bom-raw-item-${index}`} className={fieldLabelClassName}>
                      Insumo
                    </label>
                    <RawItemTypeahead
                      id={`bom-raw-item-${index}`}
                      rawItems={rawItems}
                      value={line.rawItemId}
                      onChange={(rawItemId) => updateLine(index, { rawItemId })}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor={`bom-quantity-${index}`} className={fieldLabelClassName}>
                      Quantidade
                    </label>
                    <Input
                      id={`bom-quantity-${index}`}
                      value={line.quantity}
                      onChange={(e) => updateLine(index, { quantity: e.target.value })}
                      inputMode="decimal"
                      placeholder="1"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className={fieldLabelClassName}>Custo da linha</span>
                    <p className="flex h-12 items-center text-sm font-light text-primary-900 dark:text-white">
                      {lineCostCents != null ? formatBrlCurrency(lineCostCents) : "—"}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 sm:justify-end">
                    <span className={`hidden sm:block ${fieldLabelClassName} invisible`} aria-hidden="true">
                      Ação
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => removeLine(index)}
                      aria-label={`Remover linha ${index + 1}`}
                    >
                      Remover
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button type="button" variant="outline" onClick={addLine}>
          Adicionar insumo
        </Button>
        <p className="text-sm font-light text-primary-800 dark:text-white">
          Custo total da receita:{" "}
          <span className="text-primary-900 dark:text-white">{formatBrlCurrency(totalCostCents)}</span>
        </p>
      </div>
    </section>
  );
}

export function bomLinesToInput(lines: ProductBomLine[]): Array<{ rawItemId: string; quantity: number }> {
  return lines
    .map((line) => ({
      rawItemId: line.rawItemId,
      quantity: parseQuantity(line.quantity),
    }))
    .filter((line) => line.rawItemId && line.quantity > 0);
}

export function effectiveCostFromBomLines(
  rawItems: RawItem[],
  lines: ProductBomLine[]
): number {
  const rawItemById = new Map(rawItems.map((item) => [item.id, item]));
  const bomLines = bomLinesToInput(lines)
    .map((line) => {
      const rawItem = rawItemById.get(line.rawItemId);
      if (!rawItem) return null;
      return { costCents: rawItem.costCents, quantity: line.quantity };
    })
    .filter((line): line is { costCents: number; quantity: number } => line !== null);

  return calculateBomCostCents(bomLines);
}
